import { useState } from 'react';
import { Bot, MessageSquareText, Sparkles, WandSparkles } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArtifactAiAskTab } from './ArtifactAiAskTab';
import { ArtifactAiGenerateTab } from './ArtifactAiGenerateTab';
import { ArtifactAiSuggestTab } from './ArtifactAiSuggestTab';
import { ArtifactAiSuggestionList } from './ArtifactAiSuggestionList';
import {
  ARTIFACT_AI_DISABLED_MESSAGE,
  useArtifactAi,
  type ArtifactAiToast,
  type UseArtifactAiResult,
} from '@/hooks/useArtifactAi';
import type { Artifact } from '@/lib/api';

type ArtifactAiTab = 'ask' | 'generate' | 'suggest';

export interface ArtifactAiHeadingOption {
  id: string;
  label: string;
  headingPath: string[];
}

export interface ArtifactAiTargetSeed {
  anchorBlockIndex: number;
  label: string;
  text: string;
}

interface TiptapJsonNode {
  type?: string;
  attrs?: {
    level?: number;
  };
  text?: string;
  content?: TiptapJsonNode[];
}

interface ExtractedBlock {
  type: string;
  level: number | null;
  text: string;
}

interface HeadingStackItem {
  level: number;
  text: string;
}

interface ArtifactAiPanelProps {
  artifact: Artifact;
  initialTab?: ArtifactAiTab;
  /**
   * Optional shared AI state. When provided, the panel uses it instead of
   * instantiating its own `useArtifactAi`. Used by `ArtifactDetailPage` so
   * the draft-preview banner can drive the same mutations.
   */
  ai?: UseArtifactAiResult;
  /** Currently previewed suggestion id (if any). */
  previewSuggestionId?: string | null;
  /** Called when a user clicks "Open" on a suggestion row. */
  onOpenSuggestionPreview?: (suggestionId: string) => void;
  /**
   * Suppress the in-panel "AI collaboration" header row. Set when the panel
   * is wrapped by `CollapsibleIsland` (which already provides outer chrome
   * with a title) to avoid duplicate headings.
   */
  hideHeader?: boolean;
}

const TAB_OPTIONS: {
  value: ArtifactAiTab;
  label: string;
  icon: React.ReactElement;
}[] = [
  { value: 'ask', label: 'Ask', icon: <MessageSquareText className="size-4" /> },
  { value: 'generate', label: 'Generate', icon: <Sparkles className="size-4" /> },
  { value: 'suggest', label: 'Suggest', icon: <WandSparkles className="size-4" /> },
];

export function ArtifactAiPanel({
  artifact,
  initialTab = 'ask',
  ai: aiProp,
  previewSuggestionId = null,
  onOpenSuggestionPreview,
  hideHeader = false,
}: ArtifactAiPanelProps): React.ReactElement {
  const [tabOverride, setTabOverride] = useState<ArtifactAiTab | null>(null);
  const tab = tabOverride ?? initialTab;
  // Always call the hook to preserve hook order; when `aiProp` is supplied we
  // ignore the local instance. React Query caches by key so an extra
  // subscription is cheap.
  const ownAi = useArtifactAi(artifact.id);
  const ai = aiProp ?? ownAi;
  const headings = getArtifactAiHeadingOptions(artifact.richTextJson);
  const targetSeed = getArtifactAiTargetSeed(artifact.richTextJson, artifact.plainTextCache);
  const toast: ArtifactAiToast | null = ai.aiDisabled
    ? { kind: 'error', code: 'ai_disabled', message: ARTIFACT_AI_DISABLED_MESSAGE }
    : ai.toast;

  return (
    <div className="flex flex-col gap-4 text-sm">
      {!hideHeader && (
        <div className="flex items-center gap-2 text-text-primary">
          <Bot className="size-4 text-text-secondary" />
          <span className="font-semibold">AI collaboration</span>
        </div>
      )}
      {toast && <ArtifactAiToastView toast={toast} />}
      <Tabs
        value={tab}
        onValueChange={value => {
          setTabOverride(value as ArtifactAiTab);
        }}
      >
        <TabsList className="w-full justify-start">
          {TAB_OPTIONS.map(option => (
            <TabsTrigger
              key={option.value}
              value={option.value}
              disabled={ai.aiDisabled}
              className="flex-1"
            >
              {option.icon}
              {option.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="ask">
          <ArtifactAiAskTab
            transcript={ai.transcript}
            conversationId={ai.conversationId}
            disabled={ai.aiDisabled}
            isStreaming={ai.streamingMode === 'ask'}
            onAsk={ai.ask}
          />
        </TabsContent>
        <TabsContent value="generate">
          <ArtifactAiGenerateTab
            headings={headings}
            disabled={ai.aiDisabled}
            isStreaming={ai.streamingMode === 'generate'}
            onGenerate={ai.generate}
          />
        </TabsContent>
        <TabsContent value="suggest">
          <ArtifactAiSuggestTab
            targetSeed={targetSeed}
            disabled={ai.aiDisabled}
            isStreaming={ai.streamingMode === 'suggest'}
            onSuggest={ai.suggest}
          />
        </TabsContent>
      </Tabs>
      <ArtifactAiSuggestionList
        suggestions={ai.openSuggestions}
        recentlyResolvedSuggestions={ai.recentlyResolvedSuggestions}
        loading={ai.suggestionsLoading}
        disabled={ai.aiDisabled}
        applyingSuggestionId={ai.applyingSuggestionId}
        rejectingSuggestionId={ai.rejectingSuggestionId}
        previewSuggestionId={previewSuggestionId}
        onOpenPreview={onOpenSuggestionPreview}
        onApply={ai.apply}
        onReject={ai.reject}
      />
    </div>
  );
}

function ArtifactAiToastView({ toast }: { toast: ArtifactAiToast }): React.ReactElement {
  return (
    <div
      role={toast.kind === 'error' ? 'alert' : 'status'}
      className={
        toast.kind === 'error'
          ? 'rounded-md bg-error/15 p-2 text-xs text-error'
          : 'rounded-md bg-success/15 p-2 text-xs text-success'
      }
    >
      {toast.code ? `${toast.code}: ${toast.message}` : toast.message}
    </div>
  );
}

export function getArtifactAiHeadingOptions(richTextJson: unknown): ArtifactAiHeadingOption[] {
  const stack: HeadingStackItem[] = [];
  return extractTopLevelBlocks(richTextJson).flatMap((block, index) => {
    if (block.type !== 'heading') return [];
    const text = normalizeArtifactAiText(block.text) || `Heading ${String(index + 1)}`;
    const level = block.level ?? 1;
    while (stack.length > 0 && stack[stack.length - 1].level >= level) {
      stack.pop();
    }
    stack.push({ level, text });
    const headingPath = stack.map(item => item.text);
    return [
      {
        id: `${String(index)}-${headingPath.join('/')}`,
        label: headingPath.join(' / '),
        headingPath,
      },
    ];
  });
}

export function getArtifactAiTargetSeed(
  richTextJson: unknown,
  plainTextCache: string
): ArtifactAiTargetSeed {
  const blocks = extractTopLevelBlocks(richTextJson);
  const index = blocks.findIndex(block => normalizeArtifactAiText(block.text).length > 0);
  if (index >= 0) {
    const block = blocks[index];
    return {
      anchorBlockIndex: index,
      label: `${block.type === 'heading' ? 'Heading' : 'Block'} ${String(index + 1)}`,
      text: block.text,
    };
  }

  return {
    anchorBlockIndex: -1,
    label: 'Document start',
    text: plainTextCache,
  };
}

export async function hashArtifactAiText(value: string): Promise<string> {
  const normalized = normalizeArtifactAiText(value);
  const bytes = new TextEncoder().encode(normalized);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}

function normalizeArtifactAiText(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function extractTopLevelBlocks(json: unknown): ExtractedBlock[] {
  const node = asTiptapNode(json);
  if (node === null || !Array.isArray(node.content)) return [];
  return node.content.map(child => ({
    type: child.type ?? 'unknown',
    level: typeof child.attrs?.level === 'number' ? child.attrs.level : null,
    text: extractText(child),
  }));
}

function extractText(node: TiptapJsonNode): string {
  const ownText = node.text ?? '';
  const childText = node.content?.map(extractText).join('') ?? '';
  return ownText + childText;
}

function asTiptapNode(value: unknown): TiptapJsonNode | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const attrs =
    record.attrs !== null && typeof record.attrs === 'object' && !Array.isArray(record.attrs)
      ? (record.attrs as Record<string, unknown>)
      : undefined;
  return {
    type: typeof record.type === 'string' ? record.type : undefined,
    attrs:
      attrs === undefined
        ? undefined
        : {
            level: typeof attrs.level === 'number' ? attrs.level : undefined,
          },
    text: typeof record.text === 'string' ? record.text : undefined,
    content: Array.isArray(record.content)
      ? record.content.flatMap(child => {
          const node = asTiptapNode(child);
          return node === null ? [] : [node];
        })
      : undefined,
  };
}
